//Global imports
import React, { useState } from 'react';
import { CFormInput, CFormTextarea } from '@coreui/react';

//File imports
import {
    CreateDocumentPageErrors,
    CreateDocumentPageInfoData,
    Errors,
    FormData,
    StringUtils,
} from '../../Types/types';
import { Language } from '../../../../language/English';
import CreateDocumentPageBannerHeading from '../../../../assets/images/create_document_page_banner_heading.webp';
import CreateDocumentPageBannerDescription from '../../../../assets/images/create_document_page_banner_description.webp';
import CreateDocumentPageHeading from '../../../../assets/images/create_document_page_page_heading.webp';

//Component imports
import { SeoFields, UploadPreviewImages } from '../../Components';
import {
    FieldHeadingWithHintText,
    FieldPreviewModal,
    HintText,
    LengthChecker,
} from '../../../../components';

interface CreateDocumentPageProps {
    onBlurUrlField: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    setNote: (status: boolean) => void;
    setLoader: (status: boolean) => void;
    setFolder: (name: string) => void;
    stringUtils: StringUtils;
    data: CreateDocumentPageInfoData;
    name: string;
    errors: CreateDocumentPageErrors;
    folderName: string | null;
    previewImageNote: boolean;
}

const CreateDocumentPage: React.FC<CreateDocumentPageProps> = ({
    onBlurUrlField,
    handleChange,
    handlePaste,
    onKeyDown,
    setNote,
    setLoader,
    setFolder,
    stringUtils,
    data,
    name,
    errors,
    folderName,
    previewImageNote,
}) => {
    const [fieldPreviewModalVisible, setFieldPreviewModalVisible] =
        useState(false);
    const [hintImage, setHintImage] = useState<string>('');

    const openHintModal = (imageSource: string) => {
        setHintImage(imageSource);
        setFieldPreviewModalVisible(true);
    };
    return (
        <>
            {/* Url, Google Meta Title, Meta Keyword, Meta Description Fields */}
            <SeoFields
                stringUtils={stringUtils}
                name={name}
                data={{
                    url: data?.url,
                    metaTitle: data?.metaTitle,
                    metaKeyword: data?.metaKeyword,
                    metaDescription: data?.metaDescription,
                }}
                errors={{
                    url: errors?.url,
                    metaTitle: errors?.metaTitle,
                    metaKeyword: errors?.metaKeyword,
                    metaDescription: errors?.metaDescription,
                }}
                onBlurUrlField={onBlurUrlField}
                handleChange={handleChange}
                handlePaste={handlePaste}
                onKeyDown={onKeyDown}
            />

            {/* Page Heading */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.PAGE_HEADING}
                openModal={() => {
                    openHintModal(CreateDocumentPageHeading);
                }}
            />
            <CFormInput
                value={data?.pageHeading}
                invalid={!!errors?.pageHeading}
                feedback={errors?.pageHeading}
                className="mt-2"
                type="text"
                name={stringUtils.convertToCamelCase(Language.PAGE_HEADING)}
                placeholder={Language.PAGE_HEADING}
                onChange={handleChange}
                maxLength={25}
            />
            <LengthChecker
                currentLength={data?.pageHeading?.length ?? 0}
                maxLength={25}
            />

            {/* Banner Heading */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.BANNER_HEADING}
                openModal={() => {
                    openHintModal(CreateDocumentPageBannerHeading);
                }}
            />
            <CFormInput
                value={data?.bannerHeading}
                invalid={!!errors?.bannerHeading}
                feedback={errors?.bannerHeading}
                className="mt-2"
                type="text"
                name={stringUtils.convertToCamelCase(Language.BANNER_HEADING)}
                placeholder={Language.BANNER_HEADING}
                onChange={handleChange}
                maxLength={44}
            />
            <LengthChecker
                currentLength={data?.bannerHeading?.length ?? 0}
                maxLength={44}
            />

            {/* Banner Description */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.SHORT_DESCRIPTION}
                openModal={() => {
                    openHintModal(CreateDocumentPageBannerDescription);
                }}
            />
            <HintText marginBottom="10px">
                {Language.SHORT_DESCRIPTION_HINT}
            </HintText>
            <CFormTextarea
                placeholder={Language.ENTER_SHORT_DESCRIPTION}
                onKeyDown={onKeyDown}
                onChange={handleChange}
                onPaste={handlePaste}
                value={data?.shortDescription}
                invalid={!!errors?.shortDescription}
                feedback={errors?.shortDescription}
                maxLength={160}
                style={{ height: 180 }}
                name={stringUtils.convertToCamelCase(
                    Language.SHORT_DESCRIPTION,
                )}
            />
            <LengthChecker
                currentLength={data?.shortDescription?.length ?? 0}
                maxLength={160}
            />

            {!data?.useOtherLanguageText && (
                <UploadPreviewImages
                    folderName={folderName}
                    previewImageNoteState={previewImageNote}
                    setPreviewImageNoteState={(status: boolean) => {
                        setNote(status);
                    }}
                    titleHtmlFor={stringUtils.convertToCamelCase(
                        Language.UPLOAD_PREVIEW_IMAGES,
                    )}
                    setLoader={(status: boolean) => setLoader(status)}
                    setUniqueFolderName={(name: string) => setFolder(name)}
                />
            )}

            <FieldPreviewModal
                visible={fieldPreviewModalVisible}
                onClose={() => setFieldPreviewModalVisible(false)}
                imageSrc={hintImage}
            />
        </>
    );
};

export default CreateDocumentPage;
